export type OgLocaleCode = 'en_US' | 'sr_RS';

export function ogPrimaryLocale(locale: unknown): OgLocaleCode {
    return locale === 'sr' ? 'sr_RS' : 'en_US';
}

export function ogAlternateLocale(locale: unknown): OgLocaleCode {
    return locale === 'sr' ? 'en_US' : 'sr_RS';
}

export type BreadcrumbJsonLdItem = {
    name: string;
    item: string;
};

/**
 * JSON-LD graph for a tool page: WebApplication plus optional BreadcrumbList.
 * Copy (name, description, breadcrumb names) comes from callers; this only shapes data.
 */
export function buildToolPageJsonLd(input: {
    pageUrl: string;
    applicationName: string;
    applicationDescription: string;
    siteName: string;
    siteHomeUrl?: string;
    includeWebSite?: boolean;
    breadcrumbs: BreadcrumbJsonLdItem[];
}): string {
    const graph: Record<string, unknown>[] = [];

    if (input.includeWebSite && input.siteHomeUrl) {
        graph.push({
            '@type': 'WebSite',
            '@id': `${input.siteHomeUrl}#website`,
            name: input.siteName,
            url: input.siteHomeUrl,
        });
    }

    graph.push({
        '@type': 'WebApplication',
        '@id': `${input.pageUrl}#webapplication`,
        name: input.applicationName,
        description: input.applicationDescription,
        url: input.pageUrl,
        applicationCategory: 'UtilitiesApplication',
        operatingSystem: 'Web',
        provider: {
            '@type': 'Organization',
            name: input.siteName,
        },
        offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD',
        },
    });

    if (input.breadcrumbs.length > 0) {
        graph.push({
            '@type': 'BreadcrumbList',
            '@id': `${input.pageUrl}#breadcrumb`,
            itemListElement: input.breadcrumbs.map((crumb, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                name: crumb.name,
                item: crumb.item,
            })),
        });
    }

    return JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': graph,
    });
}

/**
 * JSON-LD for legal/informational pages (WebPage).
 */
export function buildLegalPageJsonLd(input: {
    pageUrl: string;
    name: string;
    description: string;
    siteName: string;
    siteHomeUrl: string;
}): string {
    return JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${input.pageUrl}#webpage`,
        name: input.name,
        description: input.description,
        url: input.pageUrl,
        isPartOf: {
            '@type': 'WebSite',
            name: input.siteName,
            url: input.siteHomeUrl,
        },
    });
}
